import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import { useEffect, useState } from "react";
import SectionLabel from "@/components/SectionLabel";
import Reveal from "@/components/Reveal";
import RatingBar from "@/components/RatingBar";
import ReadingProgress from "@/components/ReadingProgress";
import BreedTOC from "@/components/BreedTOC";
import { useLang } from "@/i18n/LanguageContext";
import { useLocalizedBreeds, useLocalizedBreed } from "@/i18n/hooks";

const DataRow = ({ label, value }) => (
    <div className="flex items-baseline justify-between border-b border-border py-4 gap-6">
        <span className="text-sm text-ink/70">{label}</span>
        <span className="font-serif text-lg text-ink text-right kbd-num">{value}</span>
    </div>
);

const Chip = ({ children }) => (
    <span className="inline-block px-4 py-1.5 border border-earth/30 text-earth rounded-full text-xs uppercase tracking-wider">
        {children}
    </span>
);

export default function BreedProfile() {
    const { slug } = useParams();
    const { t } = useLang();
    const breeds = useLocalizedBreeds();
    const breed = useLocalizedBreed(slug);
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const onScroll = () => setScrollY(window.scrollY);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    if (!breed) return <Navigate to="/breeds" replace />;

    const totalDaily = breed.feedingCost.reduce((s, r) => s + r.daily, 0);
    const totalMonthly = breed.feedingCost.reduce((s, r) => s + r.monthly, 0);
    const totalYearly = breed.feedingCost.reduce((s, r) => s + r.yearly, 0);

    const currentIndex = breeds.findIndex((b) => b.slug === breed.slug);
    const next = breeds[(currentIndex + 1) % breeds.length];
    const prev = breeds[(currentIndex - 1 + breeds.length) % breeds.length];

    const sec = (k) => t(`breedProfile.sections.${k}`);
    const row = (k) => t(`breedProfile.rows.${k}`);
    const ratingLabels = t("breedProfile.ratingLabels") || {};

    return (
        <div data-testid={`breed-profile-${breed.slug}`} className="bg-paper">
            <ReadingProgress />
            <BreedTOC />
            {/* HERO */}
            <section className="relative h-[90vh] min-h-[560px] md:min-h-[720px] overflow-hidden" data-testid="breed-hero">
                <div
                    className="absolute inset-0 will-change-transform"
                    style={{ transform: `translate3d(0, ${scrollY * 0.3}px, 0) scale(1.08)` }}
                >
                    <img src={breed.heroImage} alt={breed.name} style={{ objectPosition: breed.objectPosition || "center" }} className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 hero-scrim" />
                <div className="absolute inset-0 grain" />
                <div className="relative h-full container-editorial flex flex-col justify-end pb-20 md:pb-28 text-white">
                    <Link to="/breeds" className="inline-flex items-center gap-2 text-white/80 text-sm mb-8 link-underline" data-testid="breed-back">
                        <ArrowLeft size={14} strokeWidth={1.5} /> {t("common.labels.allBreeds")}
                    </Link>
                    <div className="overlabel text-gold-light mb-6">{breed.classification}</div>
                    <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tight mb-6 animate-fade-up">
                        {breed.name}
                    </h1>
                    <p className="font-serif italic text-2xl md:text-3xl text-white/90 max-w-2xl animate-fade-up" style={{ animationDelay: "180ms" }}>
                        {breed.tagline}
                    </p>
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 max-w-3xl animate-fade-up" style={{ animationDelay: "320ms" }}>
                        {breed.quickFacts.map((f) => (
                            <div key={f.label}>
                                <div className="overlabel text-gold-light mb-2 text-[10px]">{f.label}</div>
                                <div className="font-serif text-2xl kbd-num">{f.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ORIGIN */}
            <section className="py-24 md:py-32 container-editorial">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
                    <Reveal className="md:col-span-4">
                        <SectionLabel number="01" label={sec("origin")} className="mb-6" />
                    </Reveal>
                    <div className="md:col-span-8">
                        <Reveal>
                            <div className="text-sm uppercase tracking-widest text-earth mb-4">{t("common.labels.nativeTo")}</div>
                            <p className="font-serif text-3xl md:text-4xl leading-tight text-ink mb-8">{breed.origin}</p>
                            <p className="text-lg leading-relaxed text-ink/75 max-w-xl">
                                {row("coat")}: <span className="text-ink">{breed.color}</span>. {row("classification")}: <span className="text-ink">{breed.classification}</span>.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* PRODUCTION */}
            <Section number="02" title={sec("production")} testid="section-production">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
                    <div>
                        <DataRow label={row("avgYield")} value={breed.production.avgYield} />
                        <DataRow label={row("peakYield")} value={breed.production.peakYield} />
                        <DataRow label={row("lactationPeriod")} value={breed.production.lactationPeriod} />
                        <DataRow label={row("milkPerLactation")} value={breed.production.milkPerLactation} />
                    </div>
                    <div>
                        <DataRow label={row("milkFat")} value={breed.production.milkFat} />
                        <DataRow label={row("snf")} value={breed.production.snf} />
                        <DataRow label={row("protein")} value={breed.production.protein} />
                    </div>
                </div>
                <div className="mt-10 pt-10 border-t border-border">
                    <div className="overlabel text-earth mb-4">{row("suitableFor")}</div>
                    <div className="flex flex-wrap gap-3">
                        {breed.production.suitableFor.map((s) => <Chip key={s}>{s}</Chip>)}
                    </div>
                </div>
            </Section>

            {/* ECONOMICS */}
            <Section number="03" title={sec("economics")} subtitle={sec("economicsSub")} testid="section-economics" alt>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                    {[
                        { label: row("calf"), value: breed.economics.calf },
                        { label: row("heifer"), value: breed.economics.heifer },
                        { label: row("firstCalving"), value: breed.economics.firstCalving },
                        { label: row("lactatingCow"), value: breed.economics.lactating },
                        { label: row("pregnantCow"), value: breed.economics.pregnant },
                    ].map((c) => (
                        <div key={c.label} className="border border-border rounded-[12px] p-6 bg-white">
                            <div className="overlabel text-earth mb-3 text-[10px]">{c.label}</div>
                            <div className="font-serif text-xl md:text-2xl text-ink kbd-num leading-tight">{c.value}</div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* FEEDING */}
            <Section number="04" title={sec("feeding")} subtitle={sec("feedingSub")} testid="section-feeding">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
                    <div>
                        <DataRow label={row("greenFodder")} value={breed.feeding.greenFodder} />
                        <DataRow label={row("dryFodder")} value={breed.feeding.dryFodder} />
                        <DataRow label={row("concentrate")} value={breed.feeding.concentrate} />
                    </div>
                    <div>
                        <DataRow label={row("mineral")} value={breed.feeding.mineral} />
                        <DataRow label={row("salt")} value={breed.feeding.salt} />
                        <DataRow label={row("water")} value={breed.feeding.water} />
                    </div>
                </div>
            </Section>

            {/* FEED COST */}
            <Section number="05" title={sec("feedCost")} subtitle={sec("feedCostSub")} testid="section-feed-cost" alt>
                <div className="overflow-x-auto -mx-6 md:mx-0">
                    <table className="w-full min-w-[720px]" data-testid="feed-cost-table">
                        <thead>
                            <tr className="border-b border-ink/20 text-earth">
                                <th className="text-left py-4 pr-6 text-xs uppercase tracking-wider font-medium">{row("feedType")}</th>
                                <th className="text-left py-4 pr-6 text-xs uppercase tracking-wider font-medium">{row("quantity")}</th>
                                <th className="text-right py-4 pr-6 text-xs uppercase tracking-wider font-medium">{row("daily")}</th>
                                <th className="text-right py-4 pr-6 text-xs uppercase tracking-wider font-medium">{row("monthly")}</th>
                                <th className="text-right py-4 text-xs uppercase tracking-wider font-medium">{row("yearly")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {breed.feedingCost.map((r) => (
                                <tr key={r.feed} className="border-b border-border hover:bg-bone/40 transition-colors">
                                    <td className="py-5 pr-6 font-serif text-lg">{feedNameKN(r.feed, t)}</td>
                                    <td className="py-5 pr-6 text-ink/70">{r.qty}</td>
                                    <td className="py-5 pr-6 text-right kbd-num">{r.daily.toLocaleString("en-IN")}</td>
                                    <td className="py-5 pr-6 text-right kbd-num">{r.monthly.toLocaleString("en-IN")}</td>
                                    <td className="py-5 text-right kbd-num">{r.yearly.toLocaleString("en-IN")}</td>
                                </tr>
                            ))}
                            <tr className="bg-forest text-paper">
                                <td className="py-5 pr-6 pl-2 font-serif text-lg">{row("totalDaily")}</td>
                                <td className="py-5 pr-6" />
                                <td className="py-5 pr-6 text-right font-serif text-xl kbd-num">₹{totalDaily.toLocaleString("en-IN")}</td>
                                <td className="py-5 pr-6 text-right font-serif text-xl kbd-num">₹{totalMonthly.toLocaleString("en-IN")}</td>
                                <td className="py-5 text-right font-serif text-xl kbd-num">₹{totalYearly.toLocaleString("en-IN")}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* LABOUR */}
            <Section number="06" title={sec("labour")} testid="section-labour">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
                    <div className="md:col-span-4">
                        <div className="overlabel text-earth mb-4">{row("totalLabour")}</div>
                        <div className="font-serif text-5xl md:text-6xl text-forest kbd-num leading-none">{breed.labour.manHours}</div>
                        <p className="mt-4 text-ink/70 leading-relaxed">{row("labourNote")}</p>
                    </div>
                    <div className="md:col-span-8">
                        {breed.labour.breakdown.map((b, i) => (
                            <div key={b.task} className="flex items-baseline justify-between border-b border-border py-4">
                                <div className="flex items-baseline gap-4">
                                    <span className="font-serif text-gold text-sm kbd-num">0{i + 1}</span>
                                    <span className="font-serif text-xl">{taskNameKN(b.task, t)}</span>
                                </div>
                                <span className="text-ink/70 kbd-num">{b.time}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* HOUSING */}
            <Section number="07" title={sec("housing")} testid="section-housing" alt>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
                    <div>
                        <DataRow label={row("coveredArea")} value={breed.housing.coveredArea} />
                        <DataRow label={row("openArea")} value={breed.housing.openArea} />
                        <DataRow label={row("ventilation")} value={breed.housing.ventilation} />
                    </div>
                    <div>
                        <DataRow label={row("floor")} value={breed.housing.floor} />
                        <DataRow label={row("drainage")} value={breed.housing.drainage} />
                        <DataRow label={row("bedding")} value={breed.housing.bedding} />
                    </div>
                </div>
            </Section>

            {/* HEALTH */}
            <Section number="08" title={sec("health")} testid="section-health">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
                    <div>
                        <DataRow label={row("diseaseResistance")} value={breed.health.diseaseResistance} />
                        <DataRow label={row("heatStress")} value={breed.health.heatStress} />
                        <DataRow label={row("tickResistance")} value={breed.health.tickResistance} />
                    </div>
                    <div>
                        <DataRow label={row("mastitis")} value={breed.health.mastitis} />
                        <DataRow label={row("veterinary")} value={breed.health.veterinary} />
                    </div>
                </div>
                <div className="mt-10 pt-10 border-t border-border">
                    <div className="overlabel text-earth mb-4">{row("commonDiseases")}</div>
                    <div className="flex flex-wrap gap-3">
                        {breed.health.commonDiseases.map((d) => <Chip key={d}>{d}</Chip>)}
                    </div>
                </div>
            </Section>

            {/* REPRODUCTION */}
            <Section number="09" title={sec("reproduction")} testid="section-reproduction" alt>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
                    <div>
                        <DataRow label={row("firstCalvingAge")} value={breed.reproduction.firstCalving} />
                        <DataRow label={row("gestation")} value={breed.reproduction.gestation} />
                        <DataRow label={row("calvingInterval")} value={breed.reproduction.calvingInterval} />
                    </div>
                    <div>
                        <DataRow label={row("fertility")} value={breed.reproduction.fertility} />
                        <DataRow label={row("insemination")} value={breed.reproduction.insemination} />
                        <DataRow label={row("natural")} value={breed.reproduction.natural} />
                    </div>
                </div>
            </Section>

            {/* ENVIRONMENT */}
            <Section number="10" title={sec("environment")} testid="section-environment">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                    {[
                        { l: row("heat"), v: breed.environment.heat },
                        { l: row("rain"), v: breed.environment.rain },
                        { l: row("cold"), v: breed.environment.cold },
                        { l: row("humidity"), v: breed.environment.humidity },
                    ].map((x) => (
                        <div key={x.l} className="p-6 border border-border rounded-[12px] bg-white">
                            <div className="overlabel text-earth mb-2 text-[10px]">{x.l}</div>
                            <div className="font-serif text-xl">{x.v}</div>
                        </div>
                    ))}
                </div>
                <div className="border-t border-border pt-8">
                    <div className="overlabel text-earth mb-3">{row("bestRegions")}</div>
                    <p className="font-serif text-2xl md:text-3xl leading-snug text-ink">{breed.environment.regions}</p>
                </div>
            </Section>

            {/* BUSINESS */}
            <Section number="11" title={sec("business")} testid="section-business" alt>
                <div className="flex flex-wrap gap-3">
                    {breed.business.map((b) => (
                        <span key={b} className="px-6 py-3 border border-forest text-forest rounded-full text-sm">{b}</span>
                    ))}
                </div>
            </Section>

            {/* RATINGS */}
            <Section number="12" title={sec("ratings")} subtitle={sec("ratingsSub")} testid="section-ratings">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
                    {Object.entries(breed.ratings).map(([k, v]) => (
                        <RatingBar key={k} label={ratingLabels[k] || k} value={v} />
                    ))}
                </div>
            </Section>

            {/* RECOMMENDATION */}
            <section className="py-24 md:py-40 bg-forest text-paper relative overflow-hidden" data-testid="section-recommendation">
                <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
                <div className="container-editorial relative">
                    <SectionLabel number="13" label={sec("recommendation")} className="mb-10" />
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
                        <div className="md:col-span-6">
                            <div className="overlabel text-gold-light mb-3">{row("bestSuited")}</div>
                            <p className="font-serif text-2xl md:text-3xl leading-snug mb-12">{breed.recommendation.bestFor}</p>
                            <div className="overlabel text-gold-light mb-3">{row("notRecommended")}</div>
                            <p className="font-serif text-2xl md:text-3xl leading-snug text-paper/85">{breed.recommendation.notFor}</p>
                        </div>
                        <div className="md:col-span-6 space-y-6">
                            <MetricRow label={row("monthlyOp")} value={breed.recommendation.monthlyOpCost} />
                            <MetricRow label={row("monthlyMilk")} value={breed.recommendation.monthlyMilk} />
                            <MetricRow label={row("expectedROI")} value={breed.recommendation.expectedROI} />
                        </div>
                    </div>

                    <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
                        <div>
                            <div className="overlabel text-gold-light mb-6">{row("advantages")}</div>
                            <ul className="space-y-4">
                                {breed.recommendation.advantages.map((a) => (
                                    <li key={a} className="flex items-start gap-4 text-paper/90 text-lg leading-relaxed">
                                        <Check size={18} strokeWidth={1.5} className="mt-1 text-gold-light flex-shrink-0" />
                                        <span>{a}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <div className="overlabel text-gold-light mb-6">{row("challenges")}</div>
                            <ul className="space-y-4">
                                {breed.recommendation.challenges.map((a) => (
                                    <li key={a} className="flex items-start gap-4 text-paper/90 text-lg leading-relaxed">
                                        <X size={18} strokeWidth={1.5} className="mt-1 text-gold-light flex-shrink-0" />
                                        <span>{a}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* GALLERY */}
            <section className="py-24 md:py-32 container-editorial" data-testid="section-gallery">
                <SectionLabel number="14" label={sec("gallery")} className="mb-10" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    {breed.gallery.map((img, i) => (
                        <div key={img} className={`relative overflow-hidden rounded-[12px] ${i === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-[3/4]"}`}>
                            <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1600ms] ease-editorial" />
                        </div>
                    ))}
                </div>
            </section>

            {/* NAV */}
            <section className="py-24 md:py-32 container-editorial border-t border-border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <Link to={`/breeds/${prev.slug}`} className="group block" data-testid="prev-breed-link">
                        <div className="overlabel text-earth mb-4 flex items-center gap-2">
                            <ArrowLeft size={12} strokeWidth={1.5} /> {t("common.buttons.previous")}
                        </div>
                        <h3 className="font-serif text-3xl md:text-5xl tracking-tight text-ink leading-none group-hover:text-forest transition-colors duration-500">
                            {prev.name}
                        </h3>
                    </Link>
                    <Link to={`/breeds/${next.slug}`} className="group block text-right md:border-l md:border-border md:pl-12" data-testid="next-breed-link">
                        <div className="overlabel text-earth mb-4 flex items-center gap-2 justify-end">
                            {t("common.buttons.next")} <ArrowRight size={12} strokeWidth={1.5} />
                        </div>
                        <h3 className="font-serif text-3xl md:text-5xl tracking-tight text-ink leading-none group-hover:text-forest transition-colors duration-500">
                            {next.name}
                        </h3>
                    </Link>
                </div>
            </section>
        </div>
    );
}

function feedNameKN(feed, t) {
    const map = {
        "Green Fodder": t("breedProfile.rows.greenFodder"),
        "Dry Fodder": t("breedProfile.rows.dryFodder"),
        "Concentrate": t("breedProfile.rows.concentrate"),
        "Mineral Mixture": t("breedProfile.rows.mineral"),
        "Salt": t("breedProfile.rows.salt"),
    };
    return map[feed] || feed;
}

function taskNameKN(task, t) {
    const map = {
        "Feeding": t("breedProfile.rows.greenFodder"), // fallback
    };
    // Local task labels — added to rows dict if needed. Fall back to English.
    const kn = t(`breedProfile.tasks.${task}`);
    if (typeof kn === "string" && kn !== `breedProfile.tasks.${task}`) return kn;
    return map[task] || task;
}

function Section({ number, title, subtitle, children, testid, alt = false }) {
    return (
        <section className={`py-24 md:py-32 ${alt ? "bg-white" : ""}`} data-testid={testid}>
            <div className="container-editorial">
                <Reveal>
                    <div className="flex items-baseline gap-6 mb-6">
                        <span className="font-serif text-gold text-sm kbd-num">{number}</span>
                        <span className="h-px flex-1 max-w-[60px] bg-gold/40" />
                    </div>
                    <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">{title}</h2>
                        {subtitle && <p className="text-ink/60 max-w-md">{subtitle}</p>}
                    </div>
                </Reveal>
                <Reveal>{children}</Reveal>
            </div>
        </section>
    );
}

function MetricRow({ label, value }) {
    return (
        <div className="border-b border-paper/20 pb-5">
            <div className="overlabel text-gold-light mb-2 text-[10px]">{label}</div>
            <div className="font-serif text-2xl md:text-3xl kbd-num">{value}</div>
        </div>
    );
}
